import React, { useState, useRef } from "react";
import { ErrorMessage, Field } from "formik";
import { IMAGE_FETCH_URL, IMAGE_URL } from "../../config";
import get from "lodash/get";
import { formatUnderscoreToSpace } from "../../utils/text-format";
import { UploadCloud, X, FileText } from "lucide-react";

export const FormDocumentField = ({
  label = "Upload",
  name = "imageField",
  className = "",
  maxSize = 5, // Default to 5MB
  ...props
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const inputRef = useRef(null);

  const uploadFile = (file, form, name) => {
    if (!file) return;

    // --- Dynamic Size Validation ---
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxSize) {
      alert(`File is too large! Maximum size allowed is ${maxSize}MB.`);
      if (inputRef.current) inputRef.current.value = ""; // Clear input
      return;
    }

    setPreviewFile(URL.createObjectURL(file));

    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", IMAGE_URL);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(percent);
      }
    };

    xhr.onloadstart = () => {
      setIsUploading(true);
      setUploadProgress(0);
    };

    xhr.onload = () => {
      setIsUploading(false);
      // Clean up the object URL to avoid memory leaks
      if (previewFile) URL.revokeObjectURL(previewFile);
      setPreviewFile(null);

      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        const filePath = response?.data?.filePath;

        const fullUrl = filePath.startsWith("http")
          ? filePath
          : `${IMAGE_FETCH_URL}${filePath}`;

        // Standard Formik way to update field
        form.setFieldValue(name, fullUrl);
      } else {
        alert("Upload failed!");
      }
    };

    xhr.onerror = () => {
      setIsUploading(false);
      setPreviewFile(null);
      alert("An error occurred during upload!");
    };

    xhr.send(formData);
  };

  const handleFileSelect = (event, form, name) => {
    const file = event.target.files[0];
    uploadFile(file, form, name);
  };

  const handleDrop = (event, form, name) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer.files[0];
    uploadFile(file, form, name);
  };

  const handleRemoveImage = (form, name) => {
    form.setFieldValue(name, "");
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const file = get(form.values, name);
        const hasError = meta.touched && meta.error;

        return (
          <div className={`flex flex-col gap-2 w-full ${className}`}>
            {label && (
              <label
                className={`text-sm font-bold tracking-wide uppercase text-slate-700 ${
                  hasError ? "text-red-500" : ""
                }`}
              >
                {formatUnderscoreToSpace(label)}
              </label>
            )}

            {!file ? (
              <div
                className={`relative w-full h-48 border-2 border-dashed rounded-xl p-4 transition-all duration-200 flex flex-col items-center justify-center cursor-pointer overflow-hidden ${
                  hasError
                    ? "border-red-400 bg-red-50/30 hover:bg-red-50/50"
                    : isDragActive
                      ? "border-indigo-500 bg-indigo-50/50 scale-[0.99]"
                      : "border-slate-200 bg-slate-50/50 hover:border-slate-300 hover:bg-slate-50"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={(e) => handleDrop(e, form, name)}
                onClick={() => !isUploading && inputRef.current.click()}
              >
                {isUploading && previewFile && (
                  <img
                    src={previewFile}
                    alt="uploading preview"
                    className="absolute inset-0 w-full h-full object-cover blur-sm scale-105 opacity-40"
                  />
                )}

                {isUploading ? (
                  <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-xs px-4">
                    <span className="text-xs font-bold text-slate-800 mb-2">
                      Uploading... {uploadProgress}%
                    </span>
                    <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden shadow-inner">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-200"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="relative z-10 flex flex-col items-center justify-center text-center">
                    <div className="p-3 bg-white border border-slate-200 rounded-xl shadow-2xs mb-3 text-primary">
                      <UploadCloud className="w-6 h-6" />
                    </div>
                    <p className="text-xs font-semibold text-slate-700 mb-1">
                      Drag & drop file or{" "}
                      <span className="text-primary hover:underline">
                        Browse
                      </span>
                    </p>
                    <p className="text-[11px] text-slate-400 font-medium">
                      Max file size: {maxSize}MB
                    </p>
                  </div>
                )}

                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*,video/mp4"
                  className="hidden"
                  onChange={(event) => handleFileSelect(event, form, name)}
                />
              </div>
            ) : (
              <div className="relative w-full h-48 border border-slate-200 rounded-xl overflow-hidden group bg-slate-900 shadow-2xs">
                {file.match(/\.(mp4|webm|ogg)$/i) ? (
                  <video
                    src={file}
                    className="w-full h-full object-cover"
                    controls
                  />
                ) : (
                  <img
                    src={file}
                    alt="Uploaded file"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                )}

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(form, name);
                  }}
                  className="absolute top-3 right-3 p-1.5 bg-slate-900/80 hover:bg-slate-900 text-white rounded-full shadow-md transition-all active:scale-95"
                  title="Remove image"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="min-h-[1.25rem] ps-1">
              <ErrorMessage name={name}>
                {(msg) => (
                  <span className="text-xs font-medium text-red-500 block">
                    {msg}
                  </span>
                )}
              </ErrorMessage>
            </div>
          </div>
        );
      }}
    </Field>
  );
};
